import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info, X } from 'lucide-react';
import { Button } from './Button';
import { getPortalRoot } from '../../utils/portal';

type ConfirmModalVariant = 'danger' | 'warning' | 'info';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmModalVariant;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'info',
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmModalProps) {
  // Empêcher le scroll du body quand la modale est ouverte
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isLoading) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onCancel, isLoading]);

  if (!open) return null;

  const variantStyles = {
    danger: {
      icon: <AlertTriangle className="h-6 w-6 text-destructive" />,
      iconBg: 'bg-destructive/10',
      confirmButtonClass: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-warning" />,
      iconBg: 'bg-warning/10',
      confirmButtonClass: '',
    },
    info: {
      icon: <Info className="h-6 w-6 text-primary" />,
      iconBg: 'bg-primary/10',
      confirmButtonClass: '',
    },
  };

  const { icon, iconBg, confirmButtonClass } = variantStyles[variant];

  // Utiliser createPortal pour rendre le modal en dehors du flux DOM
  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999]"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
    >
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" 
        onClick={!isLoading ? onCancel : undefined}
        aria-hidden="true"
      />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            className="relative bg-background rounded-lg shadow-xl w-full max-w-md transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors p-1 rounded-md hover:bg-muted"
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="p-6">
              {/* Icon */}
              <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${iconBg}`}>
                {icon}
              </div>

              {/* Title */}
              <h3 id="confirm-modal-title" className="mt-4 text-lg font-semibold text-foreground text-center">
                {title}
              </h3>

              {/* Message */}
              <p id="confirm-modal-message" className="mt-2 text-sm text-muted-foreground text-center">
                {message}
              </p>

              {/* Actions */}
              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {cancelLabel}
                </Button>
                <Button
                  variant={variant === 'danger' ? 'warning' : 'primary'}
                  onClick={onConfirm}
                  disabled={isLoading}
                  className={`w-full sm:w-auto ${confirmButtonClass}`}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Chargement...
                    </span>
                  ) : (
                    confirmLabel
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendre le modal dans un portal racine dédié au lieu d'utiliser document.body directement
  return createPortal(modalContent, getPortalRoot());
}